import React, { useEffect } from 'react'
import { createPortal } from 'react-dom'
import { X } from 'lucide-react'
import ProductReviews from './ProductReviews'

interface ProductReviewsModalProps {
    productId: string
    region?: string
    onClose: () => void
}

const ProductReviewsModal: React.FC<ProductReviewsModalProps> = ({ productId, region, onClose }) => {
    useEffect(() => {
        const originalStyle = window.getComputedStyle(document.body).overflow
        document.body.style.overflow = 'hidden'
        return () => {
            document.body.style.overflow = originalStyle
        }
    }, [])

    useEffect(() => {
        const handleEscape = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onClose()
        }

        document.addEventListener('keydown', handleEscape)
        return () => document.removeEventListener('keydown', handleEscape)
    }, [onClose])

    const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
        if (e.target === e.currentTarget) onClose()
    }

    const content = (
        <div
            className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/55 p-1.5 md:p-6"
            onClick={handleBackdropClick}
            role="dialog"
            aria-modal="true"
            aria-label="Customer reviews"
        >
            <div
                className="flex max-h-[96dvh] w-full max-w-5xl flex-col overflow-hidden rounded-[18px] bg-[#F8F5E6] shadow-2xl md:max-h-[92vh] md:rounded-[24px]"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="flex items-center justify-between gap-2 border-b border-black/10 px-3 py-2.5 md:gap-4 md:px-7 md:py-4">
                    <div>
                        <div className="font-headline text-[22px] leading-none text-black md:text-[28px]">Reviews</div>
                        <div className="mt-1 text-[13px] text-[#6B6B6B]">What families are saying about Kibu</div>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        aria-label="Close reviews"
                        className="flex h-7 w-7 flex-none items-center justify-center rounded-full text-[#6B6B6B] transition-colors hover:bg-black/5 hover:text-black focus:outline-none focus:ring-2 focus:ring-orange md:h-10 md:w-10"
                    >
                        <X className="h-[18px] w-[18px] md:h-[22px] md:w-[22px]" />
                    </button>
                </div>

                <div className="flex-1 overflow-y-auto px-0 py-2.5 md:py-7">
                    <ProductReviews productId={productId} region={region} variant="modal" className="my-0 shadow-none" />
                </div>
            </div>
        </div>
    )

    return createPortal(content, document.body)
}

export default ProductReviewsModal
